import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import * as faceapi from '@vladmandic/face-api';
import { getApiBaseUrl } from '../../../services/api';
import {
  VideoAnalysisConfig,
  DEFAULT_VIDEO_ANALYSIS_CONFIG,
  FaceAnalysisSummary,
  HeadPoseSummary
} from '../types';

export type { FaceAnalysisSummary, VideoAnalysisConfig };
export { DEFAULT_VIDEO_ANALYSIS_CONFIG };

export interface UseLocalFaceAnalysisOptions {
  attemptId?: string;
  enabled?: boolean;
  videoElement: HTMLVideoElement | null;
  cameraStream: MediaStream | null;
  config?: Partial<VideoAnalysisConfig>;
  // Backward compatibility options
  samplingIntervalMs?: number;
  enableExpressions?: boolean;
  modelBaseUrl?: string;
  onSummaryUpdated?: (summary: FaceAnalysisSummary) => void;
  onError?: (error: Error) => void;
}

export function useLocalFaceAnalysis({
  attemptId,
  enabled = false,
  videoElement,
  cameraStream,
  config: userConfig,
  samplingIntervalMs: legacySamplingInterval,
  enableExpressions: legacyEnableExpressions,
  modelBaseUrl = '/models/face-api',
  onSummaryUpdated,
  onError,
}: UseLocalFaceAnalysisOptions) {
  // Merge and validate effective configuration
  const effectiveConfig: VideoAnalysisConfig = useMemo(() => {
    const raw = {
      ...DEFAULT_VIDEO_ANALYSIS_CONFIG,
      ...(userConfig || {}),
    };
    if (legacySamplingInterval !== undefined) {
      raw.samplingIntervalMs = legacySamplingInterval;
    }
    if (legacyEnableExpressions !== undefined) {
      raw.enableExpressionAnalysis = legacyEnableExpressions;
    }

    // InputSize must be a multiple of 32 between 128 and 608 for TinyFaceDetector
    const rawInputSize = typeof raw.inputSize === 'number' ? Math.round(raw.inputSize) : 320;
    const inputSize = Math.max(128, Math.min(608, Math.round(rawInputSize / 32) * 32));

    return {
      videoAnalysisEnabled: Boolean(raw.videoAnalysisEnabled || enabled),
      samplingIntervalMs: Math.max(500, Math.min(10000, Math.round(raw.samplingIntervalMs || 2000))),
      detectorType: 'tinyFaceDetector' as const,
      inputSize,
      scoreThreshold: Math.max(0.1, Math.min(0.95, raw.scoreThreshold || 0.55)),
      maxFaces: Math.max(1, Math.min(10, Math.round(raw.maxFaces || 3))),
      enableLandmarks: raw.enableLandmarks !== undefined ? Boolean(raw.enableLandmarks) : true,
      enableHeadPose: Boolean(raw.enableHeadPose),
      enableExpressionAnalysis: Boolean(raw.enableExpressionAnalysis),
      minimumFaceWidthRatio: Math.max(0.02, Math.min(0.5, raw.minimumFaceWidthRatio || 0.1)),
      noFaceWarningSeconds: Math.max(2, Math.min(60, Math.round(raw.noFaceWarningSeconds || 10))),
      targetFaceVisiblePercentage: Math.max(10, Math.min(100, Math.round(raw.targetFaceVisiblePercentage || 80))),
      targetFaceCenteredPercentage: Math.max(10, Math.min(100, Math.round(raw.targetFaceCenteredPercentage || 70))),
      maxYawDegrees: Math.max(5, Math.min(90, Math.round(raw.maxYawDegrees || 25))),
      maxPitchDegrees: Math.max(5, Math.min(90, Math.round(raw.maxPitchDegrees || 20))),
      maxRollDegrees: Math.max(5, Math.min(90, Math.round(raw.maxRollDegrees || 20))),
      inferenceTimeoutMs: Math.max(1000, Math.min(15000, Math.round(raw.inferenceTimeoutMs || 5000))),
      storeRawFrames: false, // Strict requirement: never store or upload raw frames
    };
  }, [userConfig, legacySamplingInterval, legacyEnableExpressions, enabled]);

  const [status, setStatus] = useState<FaceAnalysisSummary['status']>(enabled ? 'INITIALIZING' : 'DISABLED');
  const [faceDetected, setFaceDetected] = useState<boolean | null>(null);
  const [faceCentered, setFaceCentered] = useState<boolean | null>(null);
  const [currentConfidence, setCurrentConfidence] = useState<number | null>(null);
  const [approximateExpression, setApproximateExpression] = useState<string | null>(null);
  const [currentFaceCount, setCurrentFaceCount] = useState<number>(0);
  const [stats, setStats] = useState<{
    validSamples: number;
    detectedCount: number;
    percentVisible: number | null;
    percentCentered: number | null;
  }>({
    validSamples: 0,
    detectedCount: 0,
    percentVisible: null,
    percentCentered: null,
  });
  const [isModelLoaded, setIsModelLoaded] = useState(false);

  // Mutable tracking references to avoid unnecessary state re-renders during inference ticks
  const isCancelledRef = useRef(false);
  const isProcessingRef = useRef(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);

  // Sample counting: valid vs failed inference samples
  const totalSamplesRef = useRef(0);
  const validSamplesRef = useRef(0);
  const failedSamplesRef = useRef(0);
  const detectedSamplesRef = useRef(0);
  const centeredSamplesRef = useRef(0);
  const multipleFacesCountRef = useRef(0);
  const maxFacesInFrameRef = useRef(0);

  // Confidence tracking
  const confidenceSumRef = useRef(0);
  const minConfidenceRef = useRef<number | null>(null);
  const maxConfidenceRef = useRef<number | null>(null);

  // Missing intervals tracking
  const missingPeriodsRef = useRef<Array<{
    startOffsetMs: number;
    endOffsetMs: number;
    durationMs: number;
    warningIssued?: boolean;
  }>>([]);
  const currentMissingStartRef = useRef<number | null>(null);
  const hasIssuedCurrentMissingWarningRef = useRef(false);

  // Head pose telemetry (approximate landmark angles)
  const headPoseSamplesRef = useRef<Array<{ yaw: number; pitch: number; roll: number }>>([]);
  const headPoseExceededRef = useRef(0);
  const headPoseSupportedRef = useRef(false);

  // Expressions distribution
  const expressionCountsRef = useRef<Record<string, number>>({
    neutral: 0,
    happy: 0,
    sad: 0,
    angry: 0,
    fearful: 0,
    disgusted: 0,
    surprised: 0,
  });

  const warningsRef = useRef<string[]>([]);
  const lastSubmittedHashRef = useRef<string>('');
  const isSubmittingRef = useRef(false);

  // 1. Model Loading with on-demand loading of only required models
  useEffect(() => {
    if (!enabled) {
      setStatus('DISABLED');
      return;
    }

    let isMounted = true;
    isCancelledRef.current = false;

    async function loadModels() {
      try {
        setStatus('INITIALIZING');

        // Always load tiny face detector
        if (!faceapi.nets.tinyFaceDetector.isLoaded) {
          await faceapi.nets.tinyFaceDetector.loadFromUri(modelBaseUrl);
        }

        // Load 68-point tiny landmarks if enabled or if head-pose is enabled
        const needsLandmarks = effectiveConfig.enableLandmarks || effectiveConfig.enableHeadPose;
        if (needsLandmarks && !faceapi.nets.faceLandmark68TinyNet.isLoaded) {
          await faceapi.nets.faceLandmark68TinyNet.loadFromUri(modelBaseUrl);
        }

        // Load expressions model if requested
        if (effectiveConfig.enableExpressionAnalysis && !faceapi.nets.faceExpressionNet.isLoaded) {
          await faceapi.nets.faceExpressionNet.loadFromUri(modelBaseUrl);
        }

        if (isMounted) {
          setIsModelLoaded(true);
          setStatus('RUNNING');
          startTimeRef.current = Date.now();
        }
      } catch (err: any) {
        console.warn('[FaceAnalysis] Model loading failed:', err);
        if (isMounted) {
          warningsRef.current.push(`Model loading error: ${err.message || 'Failed to load weights'}`);
          setStatus('FAILED');
          if (onError) onError(err);
        }
      }
    }

    loadModels();

    return () => {
      isMounted = false;
      isCancelledRef.current = true;
    };
  }, [enabled, effectiveConfig.enableLandmarks, effectiveConfig.enableHeadPose, effectiveConfig.enableExpressionAnalysis, modelBaseUrl, onError]);

  // 2. Build Summary Object with Exact Math & Edge-Case Protection
  const generateSummary = useCallback((overrideStatus?: FaceAnalysisSummary['status']): FaceAnalysisSummary => {
    const duration = startTimeRef.current ? Date.now() - startTimeRef.current : 0;
    const valid = validSamplesRef.current;
    const failed = failedSamplesRef.current;
    const detected = detectedSamplesRef.current;
    const centered = centeredSamplesRef.current;

    // Face Visibility Percentage = framesWithFace / validSampledFrames * 100
    // Exclude failed or invalid samples from the denominator.
    // If no valid samples, return null (unavailable) rather than dividing by zero.
    const faceVisiblePercentage = valid > 0
      ? Math.round((detected / valid) * 1000) / 10
      : null;

    // Face Centering Percentage = framesCentered / framesWithFace * 100
    const faceCenteredPercentage = detected > 0
      ? Math.round((centered / detected) * 1000) / 10
      : null;

    const avgConfidence = detected > 0
      ? Math.round((confidenceSumRef.current / detected) * 100) / 100
      : null;

    // Close any currently ongoing missing period
    const periods = [...missingPeriodsRef.current];
    if (currentMissingStartRef.current !== null) {
      const nowOffset = Date.now() - startTimeRef.current;
      const periodDuration = Math.max(0, nowOffset - currentMissingStartRef.current);
      periods.push({
        startOffsetMs: currentMissingStartRef.current,
        endOffsetMs: nowOffset,
        durationMs: periodDuration,
        warningIssued: periodDuration >= (effectiveConfig.noFaceWarningSeconds * 1000),
      });
    }

    // Head-pose aggregate calculation (if enabled and supported)
    let headPoseSummary: HeadPoseSummary | undefined = undefined;
    if (effectiveConfig.enableHeadPose) {
      const samples = headPoseSamplesRef.current;
      if (samples.length > 0 && headPoseSupportedRef.current) {
        let sumYaw = 0, sumPitch = 0, sumRoll = 0;
        let maxY = 0, maxP = 0, maxR = 0;
        for (const s of samples) {
          sumYaw += s.yaw;
          sumPitch += s.pitch;
          sumRoll += s.roll;
          maxY = Math.max(maxY, Math.abs(s.yaw));
          maxP = Math.max(maxP, Math.abs(s.pitch));
          maxR = Math.max(maxR, Math.abs(s.roll));
        }
        const withinCount = samples.length - headPoseExceededRef.current;
        headPoseSummary = {
          supported: true,
          samplesEvaluated: samples.length,
          withinThresholdPercentage: Math.round((withinCount / samples.length) * 1000) / 10,
          averageYaw: Math.round((sumYaw / samples.length) * 10) / 10,
          averagePitch: Math.round((sumPitch / samples.length) * 10) / 10,
          averageRoll: Math.round((sumRoll / samples.length) * 10) / 10,
          maxYaw: Math.round(maxY * 10) / 10,
          maxPitch: Math.round(maxP * 10) / 10,
          maxRoll: Math.round(maxR * 10) / 10,
          exceededCount: headPoseExceededRef.current,
          note: 'Approximate head-pose orientation derived from 68 facial landmarks. Not a proxy for attention or eye contact.'
        };
      } else {
        headPoseSummary = {
          supported: false,
          samplesEvaluated: 0,
          withinThresholdPercentage: 0,
          averageYaw: 0,
          averagePitch: 0,
          averageRoll: 0,
          maxYaw: 0,
          maxPitch: 0,
          maxRoll: 0,
          exceededCount: 0,
          note: 'Head pose estimation unsupported or landmark angles unavailable from detector.'
        };
      }
    }

    const currentStatus = overrideStatus || status;
    const finalStatus = (valid === 0 && currentStatus === 'COMPLETED') ? 'UNAVAILABLE' : currentStatus;

    return {
      status: finalStatus,
      modelVersion: 'face-api-tiny-v1',
      effectiveConfig,
      samplingIntervalMs: effectiveConfig.samplingIntervalMs,
      totalDurationMs: duration,
      totalSamples: totalSamplesRef.current,
      validSampledFrames: valid,
      failedSamplesCount: failed,
      faceDetectedSamples: detected,
      faceVisiblePercentage,
      faceCenteredPercentage,
      averageConfidence: avgConfidence,
      minConfidence: minConfidenceRef.current,
      maxConfidence: maxConfidenceRef.current,
      maxFacesDetectedInSingleFrame: maxFacesInFrameRef.current,
      multipleFacesEventsCount: multipleFacesCountRef.current,
      missingPeriodsCount: periods.length,
      missingPeriods: periods,
      headPoseSummary,
      expressionsSummary: effectiveConfig.enableExpressionAnalysis ? { ...expressionCountsRef.current } as any : undefined,
      warnings: [...warningsRef.current],
    };
  }, [effectiveConfig, status]);

  // 3. Submit Summary to Backend (Strictly no raw frames or tensors)
  const submitSummary = useCallback(async (summary: FaceAnalysisSummary) => {
    if (!attemptId || isSubmittingRef.current) return;

    // Deduplication check via content hash
    const summaryHash = `${summary.status}_${summary.validSampledFrames}_${summary.faceDetectedSamples}`;
    if (summaryHash === lastSubmittedHashRef.current && summary.status !== 'COMPLETED') {
      return;
    }

    try {
      isSubmittingRef.current = true;
      const apiBase = getApiBaseUrl();
      const res = await fetch(`${apiBase}/api/v1/attempts/${attemptId}/analysis/summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(summary),
      });
      if (res.ok) {
        lastSubmittedHashRef.current = summaryHash;
        if (onSummaryUpdated) {
          onSummaryUpdated(summary);
        }
      }
    } catch (err) {
      console.warn('[FaceAnalysis] Summary persistence failed:', err);
    } finally {
      isSubmittingRef.current = false;
    }
  }, [attemptId, onSummaryUpdated]);

  // 4. Inference Loop with Real Timeout, Overlap Prevention, and Resource Safety
  useEffect(() => {
    if (!enabled || !isModelLoaded || !videoElement || !cameraStream) {
      return;
    }

    let isHookActive = true;

    const runInferenceCycle = async () => {
      // Gate conditions: do not run if cancelled, hidden tab, camera inactive, or already running
      if (
        !isHookActive ||
        isCancelledRef.current ||
        isProcessingRef.current ||
        document.visibilityState === 'hidden' ||
        videoElement.paused ||
        videoElement.ended ||
        videoElement.readyState < 2
      ) {
        return;
      }

      isProcessingRef.current = true;
      totalSamplesRef.current += 1;

      try {
        const detectorOptions = new faceapi.TinyFaceDetectorOptions({
          inputSize: effectiveConfig.inputSize,
          scoreThreshold: effectiveConfig.scoreThreshold,
        });

        // Build inference pipeline
        const needsLandmarks = effectiveConfig.enableLandmarks || effectiveConfig.enableHeadPose;
        let detectionPromise: Promise<any>;

        if (effectiveConfig.maxFaces === 1) {
          let task = faceapi.detectSingleFace(videoElement, detectorOptions);
          if (needsLandmarks) {
            task = task.withFaceLandmarks(true) as any;
          }
          if (effectiveConfig.enableExpressionAnalysis) {
            task = task.withFaceExpressions() as any;
          }
          detectionPromise = task.run ? task.run() : (task as any);
        } else {
          let task = faceapi.detectAllFaces(videoElement, detectorOptions);
          if (needsLandmarks) {
            task = task.withFaceLandmarks(true) as any;
          }
          if (effectiveConfig.enableExpressionAnalysis) {
            task = task.withFaceExpressions() as any;
          }
          detectionPromise = task.run ? task.run() : (task as any);
        }

        // Enforce real inference timeout mechanism
        const timeoutPromise = new Promise<never>((_, reject) => {
          const id = setTimeout(() => {
            reject(new Error(`Face detection inference timed out after ${effectiveConfig.inferenceTimeoutMs}ms`));
          }, effectiveConfig.inferenceTimeoutMs);
          // Auto-clear timeout if detection completes
          detectionPromise.finally(() => clearTimeout(id)).catch(() => {});
        });

        const rawResult = await Promise.race([detectionPromise, timeoutPromise]);

        if (!isHookActive || isCancelledRef.current) return;

        // Valid sample successfully processed
        validSamplesRef.current += 1;
        const currentOffset = Date.now() - startTimeRef.current;

        // Normalize rawResult into an array of faces
        let detections: any[] = [];
        if (Array.isArray(rawResult)) {
          detections = rawResult;
        } else if (rawResult) {
          detections = [rawResult];
        }

        // Limit to maxFaces
        if (detections.length > effectiveConfig.maxFaces) {
          detections = detections.slice(0, effectiveConfig.maxFaces);
        }

        const faceCount = detections.length;
        setCurrentFaceCount(faceCount);
        maxFacesInFrameRef.current = Math.max(maxFacesInFrameRef.current, faceCount);

        if (faceCount > 1) {
          multipleFacesCountRef.current += 1;
        }

        if (faceCount >= 1) {
          // At least one face detected
          detectedSamplesRef.current += 1;
          setFaceDetected(true);

          // Select primary face (largest area or highest score)
          const primaryFace = detections[0];
          const conf = (primaryFace as any).score || (primaryFace as any).detection?.score || 0.8;
          confidenceSumRef.current += conf;
          minConfidenceRef.current = minConfidenceRef.current === null ? conf : Math.min(minConfidenceRef.current, conf);
          maxConfidenceRef.current = maxConfidenceRef.current === null ? conf : Math.max(maxConfidenceRef.current, conf);
          setCurrentConfidence(Math.round(conf * 100));

          // Calculate face positioning relative to frame
          const frameWidth = videoElement.videoWidth || videoElement.clientWidth || 640;
          const frameHeight = videoElement.videoHeight || videoElement.clientHeight || 480;
          const box = (primaryFace as any).box || (primaryFace as any).detection?.box || (primaryFace as any).alignedRect?.box;

          let isCentered = false;
          if (box && frameWidth > 0 && frameHeight > 0) {
            const centerX = (box.x + box.width / 2) / frameWidth;
            const centerY = (box.y + box.height / 2) / frameHeight;
            const widthRatio = box.width / frameWidth;

            // Central framing zone: 20% to 80% horizontal, 15% to 85% vertical
            const isInsideCenter = centerX >= 0.20 && centerX <= 0.80 && centerY >= 0.15 && centerY <= 0.85;
            const isWellSized = widthRatio >= effectiveConfig.minimumFaceWidthRatio;
            const isFullyContained = box.x >= 0 && box.y >= 0 && (box.x + box.width) <= frameWidth && (box.y + box.height) <= frameHeight;

            if (isInsideCenter && isWellSized && isFullyContained) {
              isCentered = true;
              centeredSamplesRef.current += 1;
            }

            if (!isWellSized && warningsRef.current.length < 10) {
              warningsRef.current.push(`Face framing: learner is positioned too far from camera (${Math.round(widthRatio * 100)}% frame width)`);
            }
          }
          setFaceCentered(isCentered);

          // Head-pose estimation from 68 landmarks
          if (effectiveConfig.enableHeadPose) {
            const angle = (primaryFace as any).angle;
            if (angle && typeof angle.yaw === 'number' && typeof angle.pitch === 'number' && typeof angle.roll === 'number') {
              headPoseSupportedRef.current = true;
              headPoseSamplesRef.current.push({
                yaw: angle.yaw,
                pitch: angle.pitch,
                roll: angle.roll,
              });

              // Check if rotation exceeds configured thresholds
              const exceedsThreshold =
                Math.abs(angle.yaw) > effectiveConfig.maxYawDegrees ||
                Math.abs(angle.pitch) > effectiveConfig.maxPitchDegrees ||
                Math.abs(angle.roll) > effectiveConfig.maxRollDegrees;

              if (exceedsThreshold) {
                headPoseExceededRef.current += 1;
              }
            }
          }

          // Expression classification
          if (effectiveConfig.enableExpressionAnalysis && (primaryFace as any).expressions) {
            const expressions = (primaryFace as any).expressions;
            let topExpr = 'neutral';
            let maxProb = 0;
            for (const [expr, prob] of Object.entries(expressions)) {
              if (typeof prob === 'number' && prob > maxProb) {
                maxProb = prob;
                topExpr = expr;
              }
            }
            if (expressionCountsRef.current[topExpr] !== undefined) {
              expressionCountsRef.current[topExpr] += 1;
            }
            setApproximateExpression(topExpr);
          }

          // If a missing interval was ongoing, close it
          if (currentMissingStartRef.current !== null) {
            const periodDuration = currentOffset - currentMissingStartRef.current;
            if (periodDuration >= 1000) {
              missingPeriodsRef.current.push({
                startOffsetMs: currentMissingStartRef.current,
                endOffsetMs: currentOffset,
                durationMs: periodDuration,
                warningIssued: hasIssuedCurrentMissingWarningRef.current,
              });
            }
            currentMissingStartRef.current = null;
            hasIssuedCurrentMissingWarningRef.current = false;
          }
        } else {
          // No face detected in this valid sample
          setFaceDetected(false);
          setFaceCentered(false);
          setCurrentConfidence(0);
          setApproximateExpression(null);

          // Track missing interval start
          if (currentMissingStartRef.current === null) {
            currentMissingStartRef.current = currentOffset;
          } else {
            const missingDurationSec = Math.round((currentOffset - currentMissingStartRef.current) / 1000);
            if (missingDurationSec >= effectiveConfig.noFaceWarningSeconds && !hasIssuedCurrentMissingWarningRef.current) {
              hasIssuedCurrentMissingWarningRef.current = true;
              warningsRef.current.push(`No face detected in camera stream for over ${effectiveConfig.noFaceWarningSeconds} seconds`);
            }
          }
        }

        // Update local stats display
        const validCount = validSamplesRef.current;
        const detectedCount = detectedSamplesRef.current;
        const centeredCount = centeredSamplesRef.current;
        setStats({
          validSamples: validCount,
          detectedCount,
          percentVisible: validCount > 0 ? Math.round((detectedCount / validCount) * 100) : null,
          percentCentered: detectedCount > 0 ? Math.round((centeredCount / detectedCount) * 100) : null,
        });

        // Periodic summary sync every 15 valid samples (~30 seconds)
        if (validCount % 15 === 0) {
          const currentSummary = generateSummary('RUNNING');
          submitSummary(currentSummary);
        }
      } catch (err: any) {
        console.warn('[FaceAnalysis] Inference step exception:', err);
        failedSamplesRef.current += 1;
        if (warningsRef.current.length < 15) {
          warningsRef.current.push(`Inference sample error: ${err.message || 'Execution error'}`);
        }
      } finally {
        isProcessingRef.current = false;
      }
    };

    // Scheduled interval with samplingIntervalMs (configurable, default 2000ms)
    timerRef.current = setInterval(runInferenceCycle, effectiveConfig.samplingIntervalMs);

    return () => {
      isHookActive = false;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled, isModelLoaded, videoElement, cameraStream, effectiveConfig, generateSummary, submitSummary]);

  // 5. Finalize Analysis on Completion / Unmount
  const finalizeAnalysis = useCallback(async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (!enabled || !isModelLoaded || validSamplesRef.current === 0) {
      return;
    }
    const finalSummary = generateSummary('COMPLETED');
    setStatus('COMPLETED');
    await submitSummary(finalSummary);
  }, [enabled, isModelLoaded, generateSummary, submitSummary]);

  useEffect(() => {
    return () => {
      isCancelledRef.current = true;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  return {
    status,
    isModelLoaded,
    faceDetected,
    faceCentered,
    currentConfidence,
    approximateExpression,
    currentFaceCount,
    stats,
    effectiveConfig,
    generateSummary,
    finalizeAnalysis,
  };
}
